/**
 * Community Post Detail Page
 * /community/posts/:postId - 후기 상세 페이지
 */

import { useQuery } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Link } from "wouter";
import { ArrowLeft, MessageCircle, Heart, Eye, Star, Image as ImageIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Comments } from "@/components/Comments";

interface Post {
  id: number;
  group_id: number;
  author_uid: string;
  type: string;
  title: string | null;
  body: string;
  rating: number | null;
  pinned: number;
  like_count: number;
  comment_count: number;
  view_count: number;
  created_at: string;
  updated_at: string;
  email?: string;
  name?: string;
  avatar_url?: string;
  group_title?: string;
  group_slug?: string;
  images: Array<{
    id: number;
    image_url: string;
    width: number | null;
    height: number | null;
    alt_text: string | null;
  }>;
}

export default function PostDetailPage() {
  const { user } = useAuth();
  const [, params] = useRoute<{ postId: string }>("/community/posts/:postId");
  const postId = params?.postId ? parseInt(params.postId) : null;

  // 포스트 상세 조회
  const { data: post, isLoading } = useQuery<Post>({
    queryKey: ["/api/community/posts", postId],
    queryFn: async () => {
      if (!postId) throw new Error("Invalid post ID");
      const response = await fetch(`/api/community/posts/${postId}`);
      if (!response.ok) throw new Error("Failed to fetch post");
      return response.json();
    },
    enabled: !!postId,
  });

  if (isLoading) {
    return (
      <PageContainer size="default">
        <div className="space-y-6">
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
          <div className="h-8 bg-muted rounded w-1/2 animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  if (!post) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">후기를 찾을 수 없습니다</h3>
          <p className="text-muted-foreground mb-4">요청하신 후기가 존재하지 않거나 삭제되었습니다.</p>
          <Button asChild>
            <Link href="/community/groups">모임 목록으로</Link>
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const isAuthor = user?.uid === post.author_uid;

  return (
    <>
      <Seo
        title={`${post.title || "후기"} — OZKNEWS`}
        description={post.body.substring(0, 160)}
        path={`/community/posts/${post.id}`}
        keywords={[post.group_title || "", "교민모임", "후기"]}
      />

      <div className="min-h-screen bg-background">
        <PageContainer size="default">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" asChild>
                <Link href={`/community/groups/${post.group_id}`}>
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </Button>
              <div>
                <Link
                  href={`/community/groups/${post.group_id}`}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {post.group_title || "모임"}
                </Link>
                <h1 className="text-3xl font-bold">{post.title || "후기"}</h1>
              </div>
            </div>

            {/* Post Content */}
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Author Info */}
                <div className="flex items-center gap-3 pb-4 border-b">
                  {post.avatar_url ? (
                    <img
                      src={post.avatar_url}
                      alt={post.name || post.email}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold">{post.name || post.email || "익명"}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(post.created_at), {
                        addSuffix: true,
                        locale: ko,
                      })}
                    </p>
                  </div>
                  {post.pinned === 1 && <Badge variant="default">고정</Badge>}
                  {post.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-semibold">{post.rating}</span>
                    </div>
                  )}
                </div>

                {/* Images */}
                {post.images && post.images.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {post.images.map((image) => (
                      <div key={image.id} className="relative aspect-video overflow-hidden rounded-lg">
                        <img
                          src={image.image_url}
                          alt={image.alt_text || post.title || "후기 이미지"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Body */}
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-foreground">{post.body}</p>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 pt-4 border-t text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    <span>{post.view_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    <span>{post.like_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    <span>{post.comment_count || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            {isAuthor && (
              <div className="flex gap-3">
                <Button variant="outline" asChild>
                  <Link href={`/community/posts/${postId}/edit`}>수정</Link>
                </Button>
                <Button variant="outline" className="text-destructive">
                  삭제
                </Button>
              </div>
            )}

            {/* Comments Section */}
            <Card>
              <CardContent className="p-6">
                <Comments
                  articleId={String(postId)}
                  targetType="community_post"
                  postAuthorId={post.author_uid}
                  isPostAuthor={isAuthor}
                />
              </CardContent>
            </Card>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
